import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import crowLogo from "../assets/crow-logo.png";
import { Facebook } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const phones = [
    { display: "800.642.2769", link: "+18006422769" },
    { display: "281.357.4600", link: "+12813574600" },
  ];
  const faxNumbers = [
    { display: "281.357.4608", link: "2813574608" },
  ];
  return (
    <footer className="bg-gray-900 text-gray-300" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center">
                <img
                  src={crowLogo}
                  alt="CrowCorp Logo"
                  className="w-18 h-18 object-contain"
                />
              </div>

              <div className="leading-tight">
                <div className="text-lg font-bold text-white heading-font">
                  CROW
                </div>

                <div className="text-xs text-white tracking-[0.35em] -mt-1">
                  CORPORATION
                </div>

                <div className="text-[10px] text-gray-300 tracking-wide mt-[2px]">
                  A Windlass Company
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mt-8">
              With 30 years inside the city's Loop and 20 years outside it
            </p>
          </div>

          <div>
            <h3
              className="text-white font-semibold mb-4 heading-font"
              data-testid="footer-quick-links-heading"
            >
              Quick Links
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/about"
                  className="text-sm hover:text-blue-400 transition-colors"
                  data-testid="footer-link-about"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className="text-sm hover:text-blue-400 transition-colors"
                  data-testid="footer-link-services"
                >
                  Services
                </Link>
              </li>
              <li>
                <Link
                  to="/products"
                  className="text-sm hover:text-blue-400 transition-colors"
                  data-testid="footer-link-products"
                >
                  Products
                </Link>
              </li>
              <li>
                <Link
                  to="/industries"
                  className="text-sm hover:text-blue-400 transition-colors"
                  data-testid="footer-link-industries"
                >
                  Industries
                </Link>
              </li>
              <li>
                <Link
                  to="/equipment"
                  className="text-sm hover:text-blue-400 transition-colors"
                  data-testid="footer-link-equipment"
                >
                  Equipment
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3
              className="text-white font-semibold mb-4 heading-font"
              data-testid="footer-services-heading"
            >
              Services
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/services/metal-fabrication"
                  className="text-sm hover:text-blue-400 transition-colors"
                  data-testid="footer-service-fabrication"
                >
                  Metal Fabrication
                </Link>
              </li>
              <li>
                <Link
                  to="/services/cnc-machining"
                  className="text-sm hover:text-blue-400 transition-colors"
                  data-testid="footer-service-machining"
                >
                  CNC Machining
                </Link>
              </li>
              <li>
                <Link
                  to="/services/turnkey-manufacturing"
                  className="text-sm hover:text-blue-400 transition-colors"
                  data-testid="footer-service-turnkey"
                >
                  Turnkey Manufacturing
                </Link>
              </li>
              <li>
                <Link
                  to="/services/testing"
                  className="text-sm hover:text-blue-400 transition-colors"
                  data-testid="footer-service-testing"
                >
                  Testing & NDE
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3
              className="text-white font-semibold mb-4 heading-font"
              data-testid="footer-contact-heading"
            >
              Contact Info
            </h3>

            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm" data-testid="footer-address">
                  23715 FM 2978, Tomball, TX 77375
                </span>
              </li>

              <li className="flex items-start space-x-3">
                <Phone className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />

                <span className="text-sm" data-testid="footer-phone">
                  {phones.map((phone, index) => (
                    <span key={index}>
                      <a
                        href={`tel:${phone.link}`}
                        className="hover:text-blue-400 transition-colors"
                      >
                        {phone.display}
                      </a>
                      {index !== phones.length - 1 && " | "}
                    </span>
                  ))}
                </span>
              </li>

              <li className="flex items-start space-x-3">
                <Mail className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:quote@crowcorp.com"
                  className="text-sm hover:text-blue-400 transition-colors"
                  data-testid="footer-email"
                >
                  quote@crowcorp.com
                </a>
              </li>

              <li className="flex items-start space-x-3">
                <span className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0 text-xs font-semibold">
                  F
                </span>
                <span className="text-sm" data-testid="footer-fax">
                  {faxNumbers.map((fax, index) => (
                    <span key={index}>
                      Fax: <a href={`fax:${fax.link}`} className="hover:text-blue-400 transition-colors">{fax.display}</a>
                      {index !== faxNumbers.length - 1 && " | "}
                    </span>
                  ))}
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-7 h-7 bg-white rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Facebook className="w-5 h-5 text-blue-700" />
                </div>

                <a
                  href="https://www.facebook.com/crowcorporation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm hover:text-blue-400 transition-colors flex items-center"
                  data-testid="footer-facebook-white"
                >
                  Facebook
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 text-center">
          <p className="text-sm text-gray-400" data-testid="footer-copyright">
            &copy; {currentYear} Crow Corporation. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
